import * as THREE from 'three'
import { Controls } from 'three'
import { computeRollFreeUp, type NativeControlServices } from '@/plugins/control/NativeControlUtils'
import type { CharacterSnapshot, CharacterViewBinding } from '@/shared/types/character'
import { CameraMode } from '@/shared/types/core'
import type {
  CharacterService,
  ViewEventRouter,
  ViewInputConsumer,
  ViewKeyEvent,
  ViewPointerEvent,
  ViewWheelEvent,
} from '@/shared/types/services'
import {
  createAimDirection,
  createPlanarForward,
  createPlanarRight,
  isCharacterInputKey,
  JUMP_CODES,
  MOVE_BACKWARD_CODES,
  MOVE_FORWARD_CODES,
  MOVE_LEFT_CODES,
  MOVE_RIGHT_CODES,
  RUN_CODES,
  resolvePlanarYawFromDirection,
} from './characterControlUtils'

export interface CharacterThirdPersonCameraState {
  position: THREE.Vector3
  target: THREE.Vector3
  up: THREE.Vector3
}

export interface CharacterThirdPersonControllerOptions {
  services: NativeControlServices
  characterService: CharacterService
  viewEventRouter: ViewEventRouter
  onVisualInput?: (reason: string) => void
}

const CHARACTER_THIRD_PERSON_INPUT_PRIORITY = 1_000
const MIN_CAMERA_OFFSET = 1e-4
const BODY_ROTATION_SMOOTH_TIME = 0.12
const MIN_SMOOTH_TIME = 1e-4

/** Character-third-person control mode with orbit/follow camera intent. */
export class CharacterThirdPersonController extends Controls<THREE.Camera> {
  private readonly services: NativeControlServices
  private readonly characterService: CharacterService
  private readonly viewEventRouter: ViewEventRouter
  private readonly onVisualInput?: (reason: string) => void
  private readonly viewId: string
  private readonly keyState = new Set<string>()
  private readonly pendingOrbitDelta = new THREE.Vector2()
  private readonly collisionCamera = new THREE.PerspectiveCamera()
  private readonly consumer: ViewInputConsumer

  private activeCharacterId: string | null = null
  private activePointerId: number | null = null
  private lastPointerScreen: THREE.Vector2 | null = null
  private orbitYaw = 0
  private orbitPitch = 0
  private bodyYaw = 0
  private bodyYawVelocity = 0
  private followDistance = 4.5
  private cameraDistance: number | null = null
  private cameraCoverageWaiting = false
  private safeCameraState: CharacterThirdPersonCameraState | null = null
  private pendingZoomDelta = 0
  private jumpQueued = false
  private connected = false

  constructor(options: CharacterThirdPersonControllerOptions) {
    super(new THREE.PerspectiveCamera(), null)

    if (!options.services.viewId) {
      throw new Error('CharacterThirdPersonController requires a bound viewId')
    }

    this.services = options.services
    this.characterService = options.characterService
    this.viewEventRouter = options.viewEventRouter
    this.onVisualInput = options.onVisualInput
    this.viewId = options.services.viewId
    this.enabled = false
    this.consumer = {
      name: `character-third-person:${this.viewId}`,
      priority: CHARACTER_THIRD_PERSON_INPUT_PRIORITY,
      onPointerDown: (event) => this.handlePointerDown(event),
      onPointerMove: (event) => this.handlePointerMove(event),
      onPointerUp: (event) => this.handlePointerUp(event),
      onWheel: (event) => this.handleWheel(event),
      onWinKeyDown: (event) => this.handleKeyDown(event),
      onWinKeyUp: (event) => this.handleKeyUp(event),
    }
  }

  override connect(element: HTMLElement): void {
    if (this.services.rig.mode !== CameraMode.Perspective) {
      const state = this.services.rig.getState()
      this.services.rig.setState({
        ...state,
        mode: CameraMode.Perspective,
      })
    }

    super.connect(element)
    this.connected = true
    this.resetTransientInput()
    this.viewEventRouter.removeConsumer(this.consumer)
    this.viewEventRouter.addConsumer(this.consumer)
  }

  override disconnect(): void {
    if (!this.connected) {
      return
    }

    this.connected = false
    this.viewEventRouter.removeConsumer(this.consumer)
    this.resetTransientInput()
    this.releaseControlledCharacter()
    super.disconnect()
  }

  override dispose(): void {
    this.disconnect()
  }

  override update(_delta: number): void {
    if (!this.enabled) {
      return
    }

    const binding = this.characterService.getViewBinding(this.viewId)
    if (!binding) {
      this.releaseControlledCharacter()
      return
    }

    const handle = this.characterService.get(binding.characterId)
    if (!handle) {
      this.releaseControlledCharacter()
      return
    }

    const snapshot = handle.getSnapshot()
    if (this.activeCharacterId !== binding.characterId) {
      this.releaseControlledCharacter()
      this.activeCharacterId = binding.characterId
      this.syncOrbitFromSnapshot(snapshot, binding)
      this.bodyYaw = snapshot.bodyYaw
      this.bodyYawVelocity = 0
      this.cameraDistance = this.followDistance
    }

    this.applyOrbitDelta(binding)
    this.applyZoom(binding)

    const movementDirection = this.resolveMoveDirection()
    const hasMovement = movementDirection.lengthSq() > 0
    if (hasMovement) {
      const targetBodyYaw = resolvePlanarYawFromDirection(movementDirection, this.bodyYaw)
      const smoothedYaw = smoothDampAngle(
        this.bodyYaw,
        targetBodyYaw,
        this.bodyYawVelocity,
        BODY_ROTATION_SMOOTH_TIME,
        Math.max(0, _delta)
      )
      this.bodyYaw = smoothedYaw.value
      this.bodyYawVelocity = smoothedYaw.velocity
    } else {
      this.bodyYaw = snapshot.bodyYaw
      this.bodyYawVelocity = 0
    }

    this.characterService.setCommand(binding.characterId, {
      move: hasMovement
        ? createMoveInputFromWorldDirection(movementDirection, this.bodyYaw)
        : { forward: 0, right: 0 },
      run: this.isRunPressed(),
      jump: this.consumeJump(),
      bodyYaw: this.bodyYaw,
      aimYaw: this.orbitYaw,
      aimPitch: this.orbitPitch,
    })
  }

  getCameraState(snapshot: CharacterSnapshot, delta = 0): CharacterThirdPersonCameraState {
    if (snapshot.placementPending) {
      const state = this.services.rig.getState()
      return {
        position: state.position.clone(),
        target: state.target.clone(),
        up: state.up.clone(),
      }
    }
    const target = snapshot.eyePosition.clone()
    const forward = createAimDirection(this.orbitYaw, this.orbitPitch)
    const binding = this.characterService.getViewBinding(this.viewId)
    const collision = binding?.thirdPerson.cameraCollision
    const desiredPosition = target.clone().addScaledVector(forward, -this.followDistance)
    const bounds = new THREE.Box3()
      .setFromPoints([
        target,
        desiredPosition,
        this.safeCameraState?.position ?? this.services.rig.getState().position,
      ])
      .expandByScalar((collision?.radius ?? 0) + (collision?.skinWidth ?? 0))
    this.cameraCoverageWaiting = !!(
      collision?.enabled &&
      collision.ensureCollisionCoverage &&
      !collision.ensureCollisionCoverage(bounds)
    )
    if (this.cameraCoverageWaiting) {
      const safe = this.safeCameraState ?? this.services.rig.getState()
      return { position: safe.position.clone(), target: safe.target.clone(), up: safe.up.clone() }
    }
    const distance = binding
      ? this.resolveCameraDistance(
          target,
          forward,
          this.followDistance,
          binding.thirdPerson.cameraCollision,
          delta
        )
      : this.followDistance
    const position = target.clone().addScaledVector(forward, -distance)
    this.safeCameraState = {
      position: position.clone(),
      target: target.clone(),
      up: computeRollFreeUp(forward),
    }
    return {
      position,
      target,
      up: computeRollFreeUp(forward),
    }
  }

  private handlePointerDown(event: ViewPointerEvent): boolean {
    if (!this.enabled || event.viewId !== this.viewId) {
      return false
    }

    const binding = this.characterService.getViewBinding(this.viewId)
    if (!binding || event.button !== binding.thirdPerson.dragButton) {
      return false
    }

    this.domElement?.focus()
    this.activePointerId = event.domEvent.pointerId
    this.lastPointerScreen = event.screen.clone()
    event.domEvent.preventDefault()
    this.onVisualInput?.('character.input')
    return true
  }

  private handlePointerMove(event: ViewPointerEvent): boolean {
    if (
      !this.enabled ||
      event.viewId !== this.viewId ||
      this.activePointerId !== event.domEvent.pointerId ||
      !this.lastPointerScreen
    ) {
      return false
    }

    const delta = event.screen.clone().sub(this.lastPointerScreen)
    this.lastPointerScreen.copy(event.screen)
    this.pendingOrbitDelta.add(delta)
    event.domEvent.preventDefault()
    this.onVisualInput?.('character.input')
    return true
  }

  private handlePointerUp(event: ViewPointerEvent): boolean {
    if (
      !this.enabled ||
      event.viewId !== this.viewId ||
      this.activePointerId !== event.domEvent.pointerId
    ) {
      return false
    }

    this.activePointerId = null
    this.lastPointerScreen = null
    event.domEvent.preventDefault()
    this.onVisualInput?.('character.input')
    return true
  }

  private handleWheel(event: ViewWheelEvent): boolean {
    if (!this.enabled || event.viewId !== this.viewId) {
      return false
    }

    const binding = this.characterService.getViewBinding(this.viewId)
    if (!binding) {
      return false
    }

    this.pendingZoomDelta += event.delta * binding.thirdPerson.zoomSensitivity
    event.domEvent.preventDefault()
    this.onVisualInput?.('character.input')
    return true
  }

  private handleKeyDown(event: ViewKeyEvent): boolean {
    if (!this.enabled || event.viewId !== this.viewId || !isCharacterInputKey(event.code)) {
      return false
    }

    this.keyState.add(event.code)
    if (JUMP_CODES.includes(event.code) && !event.domEvent.repeat) {
      this.jumpQueued = true
    }
    event.domEvent.preventDefault()
    this.onVisualInput?.('character.input')
    return true
  }

  private handleKeyUp(event: ViewKeyEvent): boolean {
    if (!this.enabled || event.viewId !== this.viewId || !isCharacterInputKey(event.code)) {
      return false
    }

    this.keyState.delete(event.code)
    event.domEvent.preventDefault()
    this.onVisualInput?.('character.input')
    return true
  }

  hasCameraActivity(): boolean {
    if (this.pendingOrbitDelta.lengthSq() > 0 || Math.abs(this.pendingZoomDelta) > Number.EPSILON) {
      return true
    }
    if (this.cameraCoverageWaiting || this.cameraDistance == null) {
      return false
    }
    return Math.abs(this.cameraDistance - this.followDistance) > 0.001
  }

  private applyOrbitDelta(binding: CharacterViewBinding): void {
    if (this.pendingOrbitDelta.lengthSq() <= 0) {
      return
    }

    const delta = this.pendingOrbitDelta.clone()
    this.pendingOrbitDelta.set(0, 0)
    const sensitivity = binding.thirdPerson.lookSensitivity
    this.orbitYaw -= delta.x * sensitivity
    this.orbitPitch = THREE.MathUtils.clamp(
      this.orbitPitch - delta.y * sensitivity,
      binding.thirdPerson.minPitch,
      binding.thirdPerson.maxPitch
    )
  }

  private applyZoom(binding: CharacterViewBinding): void {
    if (Math.abs(this.pendingZoomDelta) <= Number.EPSILON) {
      return
    }

    this.followDistance = THREE.MathUtils.clamp(
      this.followDistance + this.pendingZoomDelta,
      binding.thirdPerson.minDistance,
      binding.thirdPerson.maxDistance
    )
    this.pendingZoomDelta = 0
  }

  private resolveCameraDistance(
    target: THREE.Vector3,
    forward: THREE.Vector3,
    idealDistance: number,
    collision: CharacterViewBinding['thirdPerson']['cameraCollision'],
    delta: number
  ): number {
    if (!collision.enabled || !this.services.spatial) {
      this.cameraDistance = idealDistance
      return idealDistance
    }

    const blockerDistance = this.findCameraBlockerDistance(
      target,
      forward,
      idealDistance,
      collision
    )
    const desiredDistance = blockerDistance ?? idealDistance
    const previousDistance = this.cameraDistance ?? idealDistance
    const responseRate =
      desiredDistance < previousDistance ? collision.pullInSmoothing : collision.restoreSmoothing
    const alpha = delta > 0 ? 1 - Math.exp(-responseRate * delta) : 1
    this.cameraDistance = THREE.MathUtils.clamp(
      THREE.MathUtils.lerp(previousDistance, desiredDistance, alpha),
      collision.minDistance,
      idealDistance
    )
    return this.cameraDistance
  }

  private findCameraBlockerDistance(
    target: THREE.Vector3,
    forward: THREE.Vector3,
    idealDistance: number,
    collision: CharacterViewBinding['thirdPerson']['cameraCollision']
  ): number | null {
    const spatial = this.services.spatial
    if (!spatial || idealDistance <= collision.minDistance) {
      return null
    }

    const rayDirection = forward.clone().multiplyScalar(-1).normalize()
    const activeCamera = this.services.viewportCamera.getActiveCamera()
    this.collisionCamera.layers.mask = activeCamera.layers.mask
    const hit = spatial.raycastSurface({
      surfaceUsage: 'camera-control',
      ray: new THREE.Ray(target.clone(), rayDirection),
      camera: this.collisionCamera,
    })
    const tilesHit = spatial.resolveCollisionGround?.({
      ownerKind: 'tiles',
      origin: target,
      down: rayDirection,
      maxDistance: idealDistance,
      layerMask: activeCamera.layers.mask,
    })
    const distances = [hit, tilesHit]
      .filter((value) => value != null)
      .map((value) => target.distanceTo(value.point))
      .filter((distance) => distance > MIN_CAMERA_OFFSET && distance < idealDistance)
    if (!distances.length) {
      return null
    }
    const hitDistance = Math.min(...distances)

    return THREE.MathUtils.clamp(
      hitDistance - collision.radius - collision.skinWidth,
      collision.minDistance,
      idealDistance
    )
  }

  private resolveMoveDirection(): THREE.Vector3 {
    const forwardAxis = this.resolveAxis(MOVE_FORWARD_CODES, MOVE_BACKWARD_CODES)
    const rightAxis = this.resolveAxis(MOVE_RIGHT_CODES, MOVE_LEFT_CODES)
    if (forwardAxis === 0 && rightAxis === 0) {
      return new THREE.Vector3()
    }

    const direction = new THREE.Vector3()
      .addScaledVector(createPlanarForward(this.orbitYaw), forwardAxis)
      .addScaledVector(createPlanarRight(this.orbitYaw), rightAxis)
    if (direction.lengthSq() > 0) {
      direction.normalize()
    }
    return direction
  }

  private resolveAxis(positiveCodes: string[], negativeCodes: string[]): number {
    const positive = positiveCodes.some((code) => this.keyState.has(code))
    const negative = negativeCodes.some((code) => this.keyState.has(code))
    if (positive === negative) {
      return 0
    }
    return positive ? 1 : -1
  }

  private isRunPressed(): boolean {
    return RUN_CODES.some((code) => this.keyState.has(code))
  }

  private consumeJump(): boolean {
    const jump = this.jumpQueued
    this.jumpQueued = false
    return jump
  }

  private syncOrbitFromSnapshot(snapshot: CharacterSnapshot, binding: CharacterViewBinding): void {
    const target = snapshot.eyePosition
    const rigState = this.services.rig.getState()
    const offset = rigState.position.clone().sub(target)
    if (offset.lengthSq() > MIN_CAMERA_OFFSET) {
      const forward = target.clone().sub(rigState.position).normalize()
      this.orbitYaw = Math.atan2(forward.y, forward.x)
      this.orbitPitch = THREE.MathUtils.clamp(
        Math.asin(THREE.MathUtils.clamp(forward.z, -1, 1)),
        binding.thirdPerson.minPitch,
        binding.thirdPerson.maxPitch
      )
      this.followDistance = THREE.MathUtils.clamp(
        offset.length(),
        binding.thirdPerson.minDistance,
        binding.thirdPerson.maxDistance
      )
      return
    }

    this.orbitYaw = snapshot.aimYaw
    this.orbitPitch = THREE.MathUtils.clamp(
      snapshot.aimPitch,
      binding.thirdPerson.minPitch,
      binding.thirdPerson.maxPitch
    )
    this.followDistance = THREE.MathUtils.clamp(
      binding.thirdPerson.followDistance,
      binding.thirdPerson.minDistance,
      binding.thirdPerson.maxDistance
    )
  }

  private releaseControlledCharacter(): void {
    this.safeCameraState = null
    this.cameraCoverageWaiting = false
    if (!this.activeCharacterId) {
      return
    }

    const characterId = this.activeCharacterId
    this.activeCharacterId = null
    const handle = this.characterService.get(characterId)
    if (!handle) {
      return
    }

    const command = handle.getCommand()
    this.characterService.setCommand(characterId, {
      move: {
        forward: 0,
        right: 0,
      },
      run: false,
      jump: false,
      bodyYaw: command.bodyYaw,
      aimYaw: command.aimYaw,
      aimPitch: command.aimPitch,
    })
  }

  private resetTransientInput(): void {
    this.keyState.clear()
    this.pendingOrbitDelta.set(0, 0)
    this.activePointerId = null
    this.lastPointerScreen = null
    this.pendingZoomDelta = 0
    this.jumpQueued = false
  }
}

function createMoveInputFromWorldDirection(
  direction: THREE.Vector3,
  bodyYaw: number
): { forward: number; right: number } {
  const forward = createPlanarForward(bodyYaw)
  const right = createPlanarRight(bodyYaw)
  return {
    forward: direction.dot(forward),
    right: direction.dot(right),
  }
}

function smoothDampAngle(
  current: number,
  target: number,
  currentVelocity: number,
  smoothTime: number,
  delta: number
): { value: number; velocity: number } {
  const adjustedTarget = current + getShortestAngleDelta(current, target)
  return smoothDamp(
    current,
    adjustedTarget,
    currentVelocity,
    Math.max(MIN_SMOOTH_TIME, smoothTime),
    delta
  )
}

function smoothDamp(
  current: number,
  target: number,
  currentVelocity: number,
  smoothTime: number,
  delta: number
): { value: number; velocity: number } {
  if (delta <= 0) {
    return {
      value: current,
      velocity: currentVelocity,
    }
  }

  const omega = 2 / smoothTime
  const x = omega * delta
  const exp = 1 / (1 + x + 0.48 * x * x + 0.235 * x * x * x)
  const change = current - target
  const temp = (currentVelocity + omega * change) * delta
  const velocity = (currentVelocity - omega * temp) * exp
  const value = target + (change + temp) * exp
  return {
    value,
    velocity,
  }
}

function getShortestAngleDelta(current: number, target: number): number {
  return Math.atan2(Math.sin(target - current), Math.cos(target - current))
}
